import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Activity } from 'types/interfaces/activity';
import { TradeCard } from 'store/services/trade/tradeApi';
import { ACTIVITY_TYPE_CONFIG } from '../constants';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import { CardTradeTile } from 'components/CardTradeTile';
import { WantListCardOverlay } from 'screens/Public/UserProfile/components/ProfileWantListTab/WantListCardOverlay';
import SendCardTooltip from 'screens/Main/tabs/Send/components/SendCardTooltip';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { formatRelativeTime, formatAccessibleTime } from 'utils/relativeTime';
import ActivityUserBadgeFlyout from './ActivityUserBadgeFlyout';
import './ActivityFeedItem.css';

/** Activity types where "Send" (send this card to the wanter) is meaningful. */
const WANT_TYPES = new Set<Activity['type']>(['want_added', 'available']);

interface ActivityFeedItemProps {
  activity: Activity;
  isNew?: boolean;
  onAnimationEnd?: (id: string) => void;
  /** Opens the compose-trade flow for want_added/available tiles. */
  onSend?: (activity: Activity) => void;
  /** True when rendered inside an ActivityTradeGroup (by-trader view) — compact, identity-less. */
  grouped?: boolean;
  /** Size of the card-value Points component. Defaults to 'big'. */
  valueSize?: 'big' | 'small';
  /** True while the tile is animating out before being dropped from the feed. */
  isExiting?: boolean;
}

const ActivityFeedItem: React.FC<ActivityFeedItemProps> = ({
  activity,
  isNew = false,
  onAnimationEnd,
  onSend,
  grouped = false,
  valueSize = 'big',
  isExiting = false,
}) => {
  const navigate = useNavigate();
  const config = ACTIVITY_TYPE_CONFIG[activity.type];
  const [isHighlighted, setIsHighlighted] = useState(isNew);
  const [badgeFlyout, setBadgeFlyout] = useState<{ x: number; y: number } | null>(null);

  const isWantType = WANT_TYPES.has(activity.type);
  const canSend = isWantType && !!onSend;

  // Handle entry animation end
  useEffect(() => {
    if (!isNew) return;

    const timer = setTimeout(() => {
      setIsHighlighted(false);
      onAnimationEnd?.(activity.id);
    }, 1800); // 0.3s slide-in + 1.5s highlight fade

    return () => clearTimeout(timer);
  }, [isNew, activity.id, onAnimationEnd]);

  // Card click → navigate to card detail
  const handleCardClick = useCallback(() => {
    navigate(buildCardUrl(activity.card));
  }, [navigate, activity.card]);

  // Card image error handler — fallback chain
  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const currentSrc = img.src;
      const imageComingSoon = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';

      if (!img.dataset.triedLarge && currentSrc.includes('/normal/')) {
        img.dataset.triedLarge = 'true';
        img.src = currentSrc.replace('/normal/', '/large/');
        return;
      }
      if (!img.dataset.triedSmall && (currentSrc.includes('/large/') || currentSrc.includes('/normal/'))) {
        img.dataset.triedSmall = 'true';
        img.src = currentSrc.replace('/large/', '/small/').replace('/normal/', '/small/');
        return;
      }
      if (img.src !== imageComingSoon) {
        img.src = imageComingSoon;
      }
    },
    []
  );

  const { card } = activity;

  // Trader identity header (above the image) — the site-standard stacked block: avatar+flag
  // on the left; a two-row info column on the right (username + Elite badge above the point
  // balance). The relative timestamp lives at the far-right of the balance row (bottom-aligned
  // with the Points component) — moved here off the card image. The existing user-badge flyout
  // hover is preserved.
  const traderSlot = (
    <div className="masonry-card__footer-row1">
      <Link
        to={`/profile/u/${encodeURIComponent(activity.user.username)}`}
        className="activity-trader"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setBadgeFlyout({ x: rect.right + 8, y: rect.top });
        }}
        onMouseLeave={() => setBadgeFlyout(null)}
      >
        <div className="masonry-card__avatar">
          <AvatarWithFlag
            avatarUrl={activity.user.avatarUrl}
            countryCode={activity.user.countryCode}
            username={activity.user.username}
            size={28}
            isElite={activity.user?.isElite}
            showAnimatedBorder={activity.user?.showAnimatedBorder}
            borderColor1={activity.user?.borderColor1}
            borderColor2={activity.user?.borderColor2}
          />
        </div>
        <span className="activity-trader-info">
          <span className="activity-trader-name">
            <span className="activity-trader-username">{activity.user.username}</span>
            {activity.user?.isElite && <EliteBadge isElite={true} size={14} />}
          </span>
          <span className="activity-trader-row2">
            {activity.user?.points != null && (
              <Points points={activity.user.points} type="small" />
            )}
            <time
              className="activity-trader-time"
              dateTime={activity.createdAt}
              title={formatAccessibleTime(activity.createdAt)}
            >
              {formatRelativeTime(activity.createdAt)}
            </time>
          </span>
        </span>
      </Link>
      {badgeFlyout && (
        <ActivityUserBadgeFlyout
          userId={activity.user.id}
          username={activity.user.username}
          position={badgeFlyout}
          onClose={() => setBadgeFlyout(null)}
        />
      )}
    </div>
  );

  // SendCardTooltip ("username wants:" accepted printings) — only for want_added/available,
  // where the backend enriches the acceptance fields. Built directly from the activity card.
  const tooltipCard: TradeCard | null = isWantType
    ? {
        id: activity.id,
        cardId: card.id,
        name: card.name,
        set: card.setCode,
        setCode: card.setCode,
        condition: 'NEAR_MINT',
        language: 'en',
        foil: card.foil,
        finish: (card as any).finish ?? undefined,
        edition: '',
        imageUrl: card.imageUrl,
        marketPrice: 0,
        pointValue: activity.pointValue ?? 0,
        quantity: 1,
        wantListId: activity.wantEntryId ?? 0,
        scryfallId: card.scryfallId,
        acceptableSetDetails: card.acceptableSetDetails,
        acceptableConditions: card.acceptableConditions,
        acceptableLanguages: card.acceptableLanguages,
        acceptFoil: card.acceptFoil,
        acceptNonFoil: card.acceptNonFoil,
        availableInFoil: card.availableInFoil,
        availableInNonFoil: card.availableInNonFoil,
        availableLanguages: card.availableLanguages,
      }
    : null;

  // Non-grouped (By card) tiles wear the shared frosted-glass surface
  // (`.glass-container--card-details`) — the same treatment the By-trader container uses —
  // so both grouping views are visually consistent. Grouped tiles stay chrome-less inside
  // their group's GlassContainer.
  const rootClass = grouped
    ? 'masonry-card masonry-card--grouped'
    : `masonry-card glass-container glass-container--card-details${isNew ? ' masonry-card--entering' : ''}${isHighlighted ? ' masonry-card--highlight' : ''}${isExiting ? ' masonry-card--exiting' : ''}`;

  return (
    <CardTradeTile
      card={{
        name: card.name,
        imageUrl: card.imageUrl,
        href: buildCardUrl(card),
        setCode: card.setCode,
        collectorNumber: card.collectorNumber,
        foil: card.foil,
        finish: (card as any).finish ?? undefined,
      }}
      traderSlot={traderSlot}
      hideTrader={grouped}
      compact={grouped}
      cardValue={activity.pointValue}
      valueSize={valueSize}
      valueFallback={<span className="activity-tile-no-price">???</span>}
      onSend={canSend ? () => onSend?.(activity) : undefined}
      chrome="bare"
      className={rootClass}
      style={
        grouped
          ? undefined
          : ({
              // Neutral frosted border comes from the shared glass surface (matches By
              // trader); only the new-item entry glow stays type-tinted.
              '--glow-color': config ? `${config.color}44` : undefined,
            } as React.CSSProperties)
      }
      imageContainerClassName="masonry-card__image-container"
      imageClassName="masonry-card__hero-img"
      onImageError={handleImageError}
      onImageClick={handleCardClick}
      renderHover={
        tooltipCard
          ? ({ rect, close }) => (
              <>
                <WantListCardOverlay
                  cardName={card.name}
                  quantity={1}
                  addedCount={0}
                  onViewCard={() => navigate(buildCardUrl(card))}
                  onAddToTrade={() => onSend?.(activity)}
                />
                <SendCardTooltip
                  cardData={tooltipCard}
                  triggerRect={rect}
                  username={activity.user.username}
                  onClose={close}
                  onSetHover={() => {}}
                  hoveredSetCode={null}
                />
              </>
            )
          : undefined
      }
    />
  );
};

export default ActivityFeedItem;
